import { useEffect } from 'react';

/**
 * Extract the first image File from a ClipboardItem list (Clipboard API).
 * @param {ClipboardItem[]} items
 * @returns {Promise<File|null>}
 */
export async function imageFileFromClipboardItems(items) {
  if (!items?.length) {
    return null;
  }
  for (const item of items) {
    const imageType = item.types?.find((type) => type.startsWith('image/'));
    if (!imageType) continue;
    const blob = await item.getType(imageType);
    if (!blob) continue;
    const ext = imageType.split('/')[1] || 'png';
    return new File([blob], `clipboard-image.${ext}`, { type: imageType });
  }
  return null;
}

/**
 * Read an image from the system clipboard (button / mobile — needs a user gesture).
 * @returns {Promise<{ ok: true, file: File } | { ok: false, reason: 'unsupported'|'empty'|'denied'|'error' }>}
 */
export async function pasteImageFromClipboard() {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.read) {
    return { ok: false, reason: 'unsupported' };
  }
  try {
    const items = await navigator.clipboard.read();
    const file = await imageFileFromClipboardItems(items);
    if (!file) {
      return { ok: false, reason: 'empty' };
    }
    return { ok: true, file };
  } catch (error) {
    const name = error?.name || '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      return { ok: false, reason: 'denied' };
    }
    return { ok: false, reason: 'error' };
  }
}

/**
 * While `enabled`, Ctrl/Cmd+V with an image in the clipboard calls `onFiles([file])`.
 * Shared by EntityImageEditor (create forms no longer collect photos).
 */
export function useImagePaste(enabled, onFiles) {
  useEffect(() => {
    if (!enabled || typeof onFiles !== 'function') {
      return undefined;
    }

    const handlePaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (!item.type.startsWith('image/')) continue;
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          onFiles([file]);
        }
        break;
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [enabled, onFiles]);
}
